import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { fireWinCelebration } from '../../lib/celebration'
import { ArrowLeft, Flame, Share2, Shuffle, RotateCcw, HelpCircle, X, CheckCircle2, XCircle, Activity, Clock } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import Modal from '../../components/ui/Modal'
import PullToRefresh from '../../components/PullToRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { useFriends } from '../../hooks/useFriends'
import Avatar from '../../components/Avatar'
import { supabase } from '../../lib/supabaseClient'
import { cn } from '../../lib/utils'
import Board from '../../components/games/wordle/Board'
import Keyboard from '../../components/games/wordle/Keyboard'
import { usePartnerWordleAttempt, useJointStreak } from '../../components/games/wordle/hooks'
import {
  buildEmojiGrid,
  computeKeyboardState,
  computeStreak,
  isValidGuess,
  localDateKey,
  pickDailyWord,
  pickPracticeWord,
  scoreGuess,
} from '../../components/games/wordle/gameLogic'

const MAX_GUESSES = 6
const WORD_LEN = 5
const HOW_TO_SEEN_KEY = 'phoenix:wordle-howto-seen'

// Win messages tiered by how few guesses the player needed. Keeps it
// small — six tiers, indexed by `guesses.length - 1`.
const WIN_MESSAGE_KEYS = [
  'games.wordle.win.1',
  'games.wordle.win.2',
  'games.wordle.win.3',
  'games.wordle.win.4',
  'games.wordle.win.5',
  'games.wordle.win.6',
]

export function WordlePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [mode, setMode] = useState('daily') // 'daily' | 'practice'
  const todayKey = useMemo(() => localDateKey(), [])
  const dailyAnswer = useMemo(() => pickDailyWord(), [])
  const [practiceAnswer, setPracticeAnswer] = useState(() => pickPracticeWord())
  const answer = mode === 'daily' ? dailyAnswer : practiceAnswer

  // Accepted friends — one scoreboard row each. Empty list just means
  // a solo scoreboard (your own row still shows once the game ends).
  const { friends } = useFriends()

  const [guesses, setGuesses] = useState([])
  const [currentGuess, setCurrentGuess] = useState('')
  const [status, setStatus] = useState('playing') // 'playing' | 'won' | 'lost'
  const [message, setMessage] = useState(null) // transient banner
  const [streak, setStreak] = useState(0)
  const [hydrated, setHydrated] = useState(false)
  const [howToOpen, setHowToOpen] = useState(false)
  const startedAtRef = useRef(null)

  // Hydrate today's daily row from Supabase on mount. Doesn't touch
  // practice — that mode never persists.
  useEffect(() => {
    if (!user) return undefined
    if (mode !== 'daily') { setHydrated(true); return undefined }
    let alive = true
    setHydrated(false)
    supabase
      .from('games_wordle_attempts')
      .select('guesses, won')
      .eq('user_id', user.id)
      .eq('day', todayKey)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return
        if (data) {
          setGuesses(data.guesses || [])
          if (data.won === true) setStatus('won')
          else if (data.won === false) setStatus('lost')
          else setStatus('playing')
        } else {
          setGuesses([])
          setStatus('playing')
        }
        setHydrated(true)
      })
    return () => { alive = false }
  }, [user, mode, todayKey])

  // Streak — re-read whenever a daily game completes. Cap at the
  // last 60 rows; if you've kept a 60+ day streak you don't need the
  // counter to tell you, and the bigger pull would just churn bytes.
  useEffect(() => {
    if (!user) return undefined
    let alive = true
    supabase
      .from('games_wordle_attempts')
      .select('day, won')
      .eq('user_id', user.id)
      .not('won', 'is', null)
      .order('day', { ascending: false })
      .limit(60)
      .then(({ data }) => {
        if (alive) setStreak(computeStreak(data || []))
      })
    return () => { alive = false }
  }, [user, status])

  // First visit shows the how-to-play sheet. Closing it stamps
  // localStorage so we don't nag on subsequent loads.
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(HOW_TO_SEEN_KEY)
      if (!seen) setHowToOpen(true)
    } catch { /* private mode — no first-visit prompt is fine */ }
  }, [])

  // Confetti on a fresh win. Tracked via a ref of the previous status
  // so reopening the page after winning earlier (the row hydrates
  // straight to 'won' from the DB) doesn't replay the celebration —
  // we only fire on the playing → won transition the live game
  // produces. Practice wins celebrate too: winning is winning.
  const prevStatusRef = useRef(status)
  useEffect(() => {
    if (prevStatusRef.current === 'playing' && status === 'won') {
      fireWinCelebration()
    }
    prevStatusRef.current = status
  }, [status])

  const dismissHowTo = () => {
    setHowToOpen(false)
    try { window.localStorage.setItem(HOW_TO_SEEN_KEY, '1') } catch { /* noop */ }
  }

  const switchToPractice = () => {
    setMode('practice')
    setPracticeAnswer(pickPracticeWord())
    setGuesses([])
    setCurrentGuess('')
    setStatus('playing')
    setMessage(null)
    startedAtRef.current = null
  }

  const switchToDaily = () => {
    setMode('daily')
    setCurrentGuess('')
    setMessage(null)
    // Daily state will re-hydrate from DB via the effect above.
  }

  const newPracticeWord = () => {
    setPracticeAnswer(pickPracticeWord())
    setGuesses([])
    setCurrentGuess('')
    setStatus('playing')
    setMessage(null)
    startedAtRef.current = null
  }

  const gameOver = status === 'won' || status === 'lost'
  const letterStates = useMemo(() => computeKeyboardState(guesses, answer), [guesses, answer])

  const showMessage = (text, ttl = 2200) => {
    setMessage(text)
    setTimeout(() => setMessage((curr) => (curr === text ? null : curr)), ttl)
  }

  const handleLetter = useCallback((letter) => {
    if (gameOver || !hydrated) return
    setMessage(null)
    setCurrentGuess((g) => {
      if (g.length >= WORD_LEN) return g
      if (!startedAtRef.current) startedAtRef.current = Date.now()
      return g + letter
    })
  }, [gameOver, hydrated])

  const handleBackspace = useCallback(() => {
    if (gameOver || !hydrated) return
    setMessage(null)
    setCurrentGuess((g) => g.slice(0, -1))
  }, [gameOver, hydrated])

  const handleEnter = useCallback(async () => {
    if (gameOver || !hydrated) return
    if (currentGuess.length !== WORD_LEN) {
      showMessage(t('games.wordle.notEnough'))
      return
    }
    if (!isValidGuess(currentGuess)) {
      showMessage(t('games.wordle.notInList'))
      return
    }
    const nextGuesses = [...guesses, currentGuess]
    const isWin = currentGuess === answer
    const isLoss = !isWin && nextGuesses.length >= MAX_GUESSES
    const nextStatus = isWin ? 'won' : isLoss ? 'lost' : 'playing'
    const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : null

    setGuesses(nextGuesses)
    setCurrentGuess('')
    setStatus(nextStatus)

    if (isWin) showMessage(t(WIN_MESSAGE_KEYS[nextGuesses.length - 1]), 3500)
    else if (isLoss) showMessage(t('games.wordle.lostAnswer', { answer: answer.toUpperCase() }), 5000)

    // Practice mode never persists.
    if (mode !== 'daily' || !user) return

    const payload = {
      user_id: user.id,
      day: todayKey,
      guesses: nextGuesses,
      won: nextStatus === 'won' ? true : nextStatus === 'lost' ? false : null,
      duration_ms: nextStatus !== 'playing' ? durationMs : null,
      shared_grid: nextStatus !== 'playing' ? buildEmojiGrid(nextGuesses, answer) : null,
    }
    await supabase
      .from('games_wordle_attempts')
      .upsert(payload, { onConflict: 'user_id,day' })

    // Partner push triggers — fire-and-forget so a push failure can't
    // block the next guess. Two events per game:
    //   - "started"  on the FIRST guess submission
    //   - "finished" on win or lose (the last submission)
    // Anything in between is silent — we'd otherwise spam the partner
    // with a notification every row.
    const isFirstGuess = guesses.length === 0 && nextGuesses.length === 1
    const justFinished = nextStatus !== 'playing'
    if (isFirstGuess || justFinished) {
      supabase.functions
        .invoke('games-wordle-partner-push', {
          body: {
            kind: justFinished ? 'finished' : 'started',
            won: justFinished ? isWin : null,
            count: justFinished ? nextGuesses.length : null,
            day: todayKey,
          },
        })
        .catch(() => { /* push is best-effort; never block game flow */ })
    }
  }, [gameOver, hydrated, currentGuess, guesses, answer, mode, user, todayKey, t])

  const handleShare = async () => {
    const grid = buildEmojiGrid(guesses, answer)
    const score = status === 'won' ? `${guesses.length}/${MAX_GUESSES}` : `X/${MAX_GUESSES}`
    const title = mode === 'practice'
      ? t('games.wordle.shareTitlePractice', { score })
      : t('games.wordle.shareTitleDaily', { date: todayKey, score })
    const text = `${title}\n\n${grid}`
    try {
      await navigator.clipboard.writeText(text)
      showMessage(t('games.wordle.copied'))
    } catch {
      showMessage(t('games.wordle.copyFailed'))
    }
  }

  const goBack = () => navigate('/')
  const handleRefresh = () => Promise.resolve()

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <h1
            className="flex-1 min-w-0 text-lg font-bold text-foreground truncate"
            dir="auto"
          >
            {t('games.wordle.title')}
          </h1>
          {streak > 0 && mode === 'daily' && (
            <span
              className="inline-flex items-center gap-1 text-sm font-bold text-app-primary-500"
              dir="ltr"
              title={t('games.wordle.streakLabel')}
            >
              <Flame className="w-4 h-4" />
              {streak}
            </span>
          )}
          <button
            type="button"
            onClick={() => setHowToOpen(true)}
            aria-label={t('games.wordle.howToPlay')}
            className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2" dir="auto">
          <button
            type="button"
            onClick={switchToDaily}
            className={cn(
              'flex-1 px-3 py-2 rounded-md text-xs font-semibold border-2 transition-colors',
              mode === 'daily'
                ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                : 'border-border text-muted-foreground hover:bg-accent',
            )}
          >
            {t('games.wordle.modeDaily')}
          </button>
          <button
            type="button"
            onClick={mode === 'practice' ? newPracticeWord : switchToPractice}
            className={cn(
              'flex-1 px-3 py-2 rounded-md text-xs font-semibold border-2 inline-flex items-center justify-center gap-1.5 transition-colors',
              mode === 'practice'
                ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                : 'border-border text-muted-foreground hover:bg-accent',
            )}
          >
            {mode === 'practice'
              ? <><Shuffle className="w-3.5 h-3.5" />{t('games.wordle.modePracticeNext')}</>
              : <><Shuffle className="w-3.5 h-3.5" />{t('games.wordle.modePractice')}</>}
          </button>
        </div>

        {/* PartnerPanel removed — the live scoreboard below the board
            (rendered always in daily mode now) folds in everything it
            used to surface: partner status icon, joint streak, and
            now the emoji map for each player too. */}

        {/* Toast / message banner */}
        <div className="h-7 flex items-center justify-center" aria-live="polite">
          {message && (
            <span className="text-sm font-medium text-foreground bg-card border border-border px-3 py-1 rounded-md shadow-sm" dir="auto">
              {message}
            </span>
          )}
        </div>

        {/* Board */}
        <Board
          guesses={guesses}
          currentGuess={currentGuess}
          answer={answer}
          gameOver={gameOver}
        />

        {/* Practice mode end-of-game: simple celebration + Play Again. */}
        {gameOver && mode === 'practice' && (
          <Card className="border-app-primary-500/30 bg-app-primary-500/5">
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground" dir="auto">
                  {status === 'won'
                    ? t('games.wordle.youWon', { count: guesses.length })
                    : t('games.wordle.youLost', { answer: answer.toUpperCase() })}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5" dir="auto">
                  {t('games.wordle.practiceHint')}
                </p>
              </div>
              <Button onClick={newPracticeWord} size="sm" variant="outline">
                <RotateCcw className="w-4 h-4 me-1.5" />
                {t('games.wordle.playAgain')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Daily mode live scoreboard. Your row first with whatever
            guesses you've submitted, then one row per accepted friend
            (each subscribes to its friend's today row in realtime).
            Each row shows the emoji map (colour squares per submitted
            guess) without revealing letters, so everyone stays aware
            of each other's progress without spoiling. Headline +
            Share button only land once you finish. */}
        {mode === 'daily' && (friends.length > 0 || gameOver) && (
          <Card className="border-app-primary-500/30 bg-app-primary-500/5">
            <CardContent className="p-3 sm:p-4 space-y-3">
              {gameOver && (
                <p className="text-sm font-semibold text-foreground" dir="auto">
                  {status === 'won'
                    ? t('games.wordle.youWon', { count: guesses.length })
                    : t('games.wordle.youLost', { answer: answer.toUpperCase() })}
                </p>
              )}
              <div className="space-y-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500" dir="auto">
                  {t('games.wordle.scoreboard.title')}
                </p>
                <ScoreboardRow
                  userId={user?.id}
                  label={t('games.wordle.partner.you')}
                  status={status}
                  guesses={guesses}
                  answer={answer}
                />
                {friends.map((f) => (
                  <FriendScoreboardRow
                    key={f.other_user_id}
                    friend={f}
                    day={todayKey}
                    answer={answer}
                    myUserId={user?.id}
                    myStatus={status}
                  />
                ))}
              </div>
              {gameOver && (
                <Button onClick={handleShare} size="sm" className="w-full">
                  <Share2 className="w-4 h-4 me-1.5" />
                  {t('games.wordle.share')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Keyboard */}
        <Keyboard
          letterStates={letterStates}
          onLetter={handleLetter}
          onEnter={handleEnter}
          onBackspace={handleBackspace}
          disabled={gameOver || !hydrated}
        />
      </div>

      {howToOpen && <HowToPlayDialog onClose={dismissHowTo} t={t} />}
    </PullToRefresh>
  )
}

// Single row in the daily-mode scoreboard. Shows a status icon, the
// player's name, and (once finished) their guess count, plus the
// emoji grid of every guess they've submitted so far. The grid hides
// letters but does imply the row count by its length — that's the
// trade-off the owner accepted when asking for live maps; the
// previous "no count revealed during play" contract is superseded.
// Per-cell tone for the mini scoreboard map. Same colour vocabulary
// as the main Board cells (emerald/amber/stone) so the scoreboard
// reads as a faithful miniature of the actual game state — not the
// flat emoji glyphs that were here before.
const MINI_TONE = {
  correct: 'bg-emerald-500 dark:bg-emerald-600',
  present: 'bg-amber-500 dark:bg-amber-600',
  absent:  'bg-stone-500 dark:bg-stone-600',
}

function MiniBoard({ guesses, answer }) {
  return (
    <div className="flex flex-col gap-1" dir="ltr">
      {guesses.map((g, rowIdx) => {
        const scored = scoreGuess(g, answer)
        return (
          <div key={rowIdx} className="flex gap-1">
            {scored.map((state, i) => (
              <span
                key={i}
                className={cn('w-5 h-5 sm:w-6 sm:h-6 rounded-sm', MINI_TONE[state])}
              />
            ))}
          </div>
        )
      })}
    </div>
  )
}

// One scoreboard row per friend. A child component so each friend
// gets their own realtime subscription + joint-streak walk via the
// existing per-user hooks — no fan-in hook needed.
function FriendScoreboardRow({ friend, day, answer, myUserId, myStatus }) {
  const attempt = usePartnerWordleAttempt(friend.other_user_id, day)
  const jointStreak = useJointStreak(
    myUserId,
    friend.other_user_id,
    `${myStatus}|${attempt.status}`,
  )
  const label = friend.display_name
    || (friend.username ? `@${friend.username}` : `${friend.other_user_id.slice(0, 8)}…`)
  return (
    <ScoreboardRow
      userId={friend.other_user_id}
      profile={friend}
      label={label}
      status={attempt.status}
      guesses={attempt.row?.guesses}
      answer={answer}
      streak={jointStreak}
    />
  )
}

function ScoreboardRow({ userId, profile = null, label, status, guesses, answer, streak = 0 }) {
  const Icon = status === 'won'
    ? CheckCircle2
    : status === 'lost'
      ? XCircle
      : status === 'playing'
        ? Activity
        : Clock
  const tone = status === 'won'
    ? 'text-emerald-500'
    : status === 'lost'
      ? 'text-stone-500'
      : status === 'playing'
        ? 'text-app-primary-500 animate-pulse'
        : 'text-muted-foreground'
  const showCount = status === 'won' || status === 'lost'
  const hasMap = Array.isArray(guesses) && guesses.length > 0 && !!answer
  return (
    <div className="space-y-2.5">
      {/* Identity row: icon → avatar → label → score, all on the
          leading edge so they read as one unit ("✓ [avatar] إنت 5/6").
          No flex-1, no justify-between — items take their natural
          widths and flow together. */}
      <div className="flex items-center gap-2 text-sm">
        <Icon className={`w-4 h-4 flex-shrink-0 ${tone}`} />
        <Avatar userId={userId} profile={profile} size={20} />
        <span className="text-foreground font-medium truncate" dir="auto">{label}</span>
        {showCount && (
          <span className="text-foreground font-mono tabular-nums font-bold flex-shrink-0" dir="ltr">
            {status === 'won' ? `${guesses.length}/6` : 'X/6'}
          </span>
        )}
        {streak > 0 && (
          <span
            className="inline-flex items-center gap-0.5 text-[11px] font-bold text-app-primary-500 flex-shrink-0"
            dir="ltr"
          >
            <Flame className="w-3 h-3" />
            {streak}
          </span>
        )}
      </div>
      {/* Mini map: outer flex with ps-6 (padding-inline-start) so the
          indent flips correctly with direction. dir="ltr" on the
          inner board pins the cell order to the word's letter order
          regardless of UI locale. */}
      {hasMap && (
        <div className="flex ps-6">
          <MiniBoard guesses={guesses} answer={answer} />
        </div>
      )}
    </div>
  )
}

// How-to-play body. Modal shell (scrim, centering, scroll lock, ESC)
// comes from the shared <Modal> — see components/ui/Modal.jsx.
function HowToPlayDialog({ onClose, t }) {
  return (
    <Modal open onClose={onClose} ariaLabel={t('games.wordle.howToPlay')}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">
          {t('games.wordle.howToPlay')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('settings.close')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <ul className="space-y-2 text-sm text-foreground" dir="auto">
        <li>{t('games.wordle.howTo.line1')}</li>
        <li>{t('games.wordle.howTo.line2')}</li>
        <li className="flex items-center gap-2">
          <span className="inline-flex w-6 h-6 items-center justify-center rounded bg-emerald-500 text-white font-bold text-xs">A</span>
          {t('games.wordle.howTo.green')}
        </li>
        <li className="flex items-center gap-2">
          <span className="inline-flex w-6 h-6 items-center justify-center rounded bg-amber-500 text-white font-bold text-xs">A</span>
          {t('games.wordle.howTo.yellow')}
        </li>
        <li className="flex items-center gap-2">
          <span className="inline-flex w-6 h-6 items-center justify-center rounded bg-stone-500 text-white font-bold text-xs">A</span>
          {t('games.wordle.howTo.gray')}
        </li>
        <li>{t('games.wordle.howTo.daily')}</li>
      </ul>
      <Button onClick={onClose} className="w-full mt-4">
        {t('games.wordle.howTo.cta')}
      </Button>
    </Modal>
  )
}
